import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useDeferredVersionAttrSelection } from '@/hooks/use-deferred-version-attr-selection';
import { useMemo, useState } from 'react';
import { getVersionBadgeColors } from './zyversion-color';

export interface VersionBadgeColors {
    bg: string;
    hover: string;
}

export interface ZyVersionBadgeProps {
    version: string;
    allowedVersions?: string[];
    onVersionChange?: (version: string) => void;
    /** Fired when the version picker opens or fully closes (after apply commit when applicable). */
    onVersionPopoverOpenChange?: (open: boolean) => void;
    colors?: VersionBadgeColors;
    className?: string;
    emptyLabel?: string;
}

const BADGE_BASE_CLASS = 'rounded px-2 py-0.5 text-xs font-medium text-white';

export function ZyVersionBadge({
    version,
    allowedVersions = [],
    onVersionChange,
    onVersionPopoverOpenChange,
    colors: colorsProp,
    className = '',
    emptyLabel = 'version',
}: ZyVersionBadgeProps) {
    const canEdit = Boolean(onVersionChange) && allowedVersions.length > 0;

    const {
        popoverOpen,
        handlePopoverOpenChange,
        applyPending,
        cancelPending,
        toggleAllowedVersion,
        canApplyPending,
        isAllowedVersionChecked,
        legacyVersions,
    } = useDeferredVersionAttrSelection({
        appliedVersion: version,
        allowedVersions,
        onCommit: onVersionChange ?? (() => {}),
        onPopoverOpenChange: open => onVersionPopoverOpenChange?.(open),
        onPopoverEditingEnd: () => onVersionPopoverOpenChange?.(false),
    });

    const colors = useMemo(() => colorsProp ?? getVersionBadgeColors(version), [colorsProp, version]);
    const label = version.trim() || emptyLabel;

    const [hovered, setHovered] = useState(false);

    if (!canEdit) {
        const readOnlyLabel = version.trim() || emptyLabel;
        return (
            <span
                className={`${BADGE_BASE_CLASS} shadow-sm ${className}`}
                style={{ backgroundColor: colors.bg }}
            >
                {readOnlyLabel}
            </span>
        );
    }

    return (
        <Popover
            open={popoverOpen}
            onOpenChange={handlePopoverOpenChange}
        >
            <PopoverTrigger asChild>
                <button
                    type="button"
                    contentEditable={false}
                    className={`z-10 cursor-pointer ${BADGE_BASE_CLASS} focus:outline-none focus:ring-2 focus:ring-offset-1 ${className}`}
                    style={{ backgroundColor: hovered ? colors.hover : colors.bg } as React.CSSProperties}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    onClick={e => {
                        e.stopPropagation();
                    }}
                >
                    {label}
                </button>
            </PopoverTrigger>
            {popoverOpen ? (
                <PopoverContent
                    align="start"
                    className="w-56 p-2 duration-0 animate-none data-[state=closed]:animate-none data-[state=open]:animate-none"
                    onOpenAutoFocus={e => e.preventDefault()}
                >
                    <p className="mb-2 text-xs font-medium text-gray-600 dark:text-gray-400">Versions</p>
                    <ul className="flex max-h-48 flex-col gap-2 overflow-y-auto">
                        {legacyVersions.map(legacyVersion => (
                            <li key={`legacy-${legacyVersion}`}>
                                <label className="flex cursor-not-allowed items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                    <Checkbox
                                        checked
                                        disabled
                                    />
                                    <span>{legacyVersion} (not in subject list)</span>
                                </label>
                            </li>
                        ))}
                        {allowedVersions.map(allowedVersion => (
                            <li key={allowedVersion}>
                                <label className="flex cursor-pointer items-center gap-2 text-sm">
                                    <Checkbox
                                        checked={isAllowedVersionChecked(allowedVersion)}
                                        onCheckedChange={() => toggleAllowedVersion(allowedVersion)}
                                    />
                                    <span>{allowedVersion}</span>
                                </label>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-2 flex justify-end gap-2 border-t border-gray-200 pt-2 dark:border-gray-600">
                        <Button
                            type="button"
                            data-style="primary"
                            size="small"
                            disabled={!canApplyPending}
                            onClick={applyPending}
                        >
                            Apply
                        </Button>
                        <Button
                            type="button"
                            data-style="secondary"
                            size="small"
                            onClick={cancelPending}
                        >
                            Cancel
                        </Button>
                    </div>
                </PopoverContent>
            ) : null}
        </Popover>
    );
}
